import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, CheckCircle2, History, Trophy, XCircle, Archive, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AdminNotes } from "@/components/AdminNotes";
import { TimelineList } from "@/components/AdminTimeline";
import { enterpriseQuoteForSize, tiersFromApi, money } from "@/lib/pricingCatalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const mailtoForQuote = (q, proposal = null) => {
  const first = (q.contact_name || "there").split(" ")[0];
  const subject = `T.A.C.C. for ${q.company_name} — next step`;
  const proposalLine = proposal
    ? `\n\nTo give you a real number now: for a team around ${proposal.seats.toLocaleString()} people, T.A.C.C. runs about $${proposal.perSeatYear.toLocaleString()} per person per year — roughly $${proposal.annual.toLocaleString()} a year for your whole organisation, all eight modules included. That's a starting figure we tune to your exact headcount on the call.`
    : "";
  const body = `Hi ${first},\n\nThanks for reaching out about T.A.C.C. for ${q.company_name} (${q.company_size} people${q.interested_in?.length ? `, interested in ${q.interested_in.join(", ")}` : ""}).\n\nSimplest next step is a 20-minute call — I'll walk you through the eight modules, how teams roll them out shift by shift, and give you a straight number for your seats.${proposalLine}\n\nTwo times that work for you?\n\n— Rich Culmer\nFounder, T.A.C.C. — Through Adversity Comes Change\nhttps://tacc.love/b2b`;
  return `mailto:${q.work_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[#E7DFD5] text-sm">
    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C] pt-0.5">{k}</span>
    <span className="text-right text-[#44403C] break-words min-w-0">{v || "—"}</span>
  </div>
);

const STATUS_TONE = { won: "text-emerald-400", lost: "text-red-400", closed: "text-[#78716C]", contacted: "text-[#B45309]", new: "text-[#B45309]" };

export const QuoteDetailDialog = ({ quote, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [status, setStatus] = useState(null);
  const [tiers, setTiers] = useState(null);
  useEffect(() => { setStatus(null); }, [quote?.id]);
  useEffect(() => {
    if (!quote) return;
    axios.get(`${API}/admin/pricing/enterprise`).then((r) => setTiers(tiersFromApi(r.data.tiers))).catch(() => {});
  }, [quote]);
  if (!quote) return null;
  const q = { ...quote, status: status ?? quote.status ?? "new" };
  const finished = ["won", "lost", "closed"].includes(q.status);
  const proposal = enterpriseQuoteForSize(q.company_size, tiers || undefined);

  const setLeadStatus = async (next) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/leads/${quote.id}/status`, { status: next });
      setStatus(next);
      toast.success(next === "new" ? "Lead reopened." : `Marked ${next}.`, { description: next === "new" ? "It's back on the waiting list." : "It drops off Hot Leads and the digest." });
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update the lead.");
    } finally {
      setBusy(false);
    }
  };

  const reply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/leads/${q.id}/reply`);
      toast[r.data.email_ok ? "success" : "message"](r.data.email_ok ? `Intro sent to ${q.contact_name}.` : "Marked contacted — the email didn't send (see Emails tab).");
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the reply.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(quote)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#F59E0B]/40 text-[#1C1917] max-w-lg p-6 md:p-8 max-h-[90vh] overflow-y-auto" data-testid="quote-detail-dialog">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309] mb-1">B2B quote · <span className={STATUS_TONE[q.status] || "text-[#B45309]"} data-testid="quote-detail-status">{q.status}</span></div>
        <DialogTitle className="font-display text-3xl tracking-tight leading-none" data-testid="quote-detail-company">{q.company_name}</DialogTitle>
        <DialogDescription className="text-[#78716C] text-sm">{q.contact_name} · <a href={`mailto:${q.work_email}`} className="text-[#B45309]">{q.work_email}</a></DialogDescription>
        <div className="mt-2">
          <Row k="Received" v={q.created_at ? new Date(q.created_at).toLocaleString() : "—"} />
          <Row k="Company size" v={q.company_size} />
          <Row k="Industry" v={q.industry} />
          <Row k="Tier" v={q.tier} />
          <Row k="Interested in" v={(q.interested_in || []).join(", ")} />
          {q.replied_at && <Row k="Replied" v={new Date(q.replied_at).toLocaleString()} />}
        </div>
        <div className="mt-4">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716C] mb-2">Their message</div>
          <p className="text-sm text-[#44403C] leading-relaxed whitespace-pre-wrap bg-black/5 border border-[#E7DFD5] p-4" data-testid="quote-detail-message">{q.message}</p>
        </div>
        {proposal && (
          <div className="mt-4 bg-[#F59E0B]/[0.07] border border-[#F59E0B]/30 rounded-lg p-4" data-testid="quote-proposal">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#B45309] mb-1">Proposal ready to send</div>
            <p className="text-sm text-[#44403C] leading-relaxed">
              ~{proposal.seats.toLocaleString()} people × {money(proposal.perSeatYear)}/seat/yr ={" "}
              <span className="text-[#B45309] font-display text-lg">{money(proposal.annual)}/year</span>
            </p>
            <p className="text-[11px] text-[#A8A29E] mt-1">This number is baked into both reply options below. Change the rates in the Pricing &amp; Products tab.</p>
          </div>
        )}
        <div className="flex flex-wrap gap-3 pt-4">
          <button onClick={reply} disabled={busy || q.status === "contacted"} className="btn-ember text-xs disabled:opacity-50" data-testid="quote-detail-reply">
            {q.status === "contacted" ? <><CheckCircle2 size={13} /> Replied</> : <><Send size={13} /> Reply now</>}
          </button>
          <a href={mailtoForQuote(q, proposal)} className="btn-outline text-xs" data-testid="quote-detail-mailto"><Mail size={13} /> Open in my email</a>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-4" data-testid="quote-status-actions">
          <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#A8A29E] mr-1">Outcome</span>
          {finished ? (
            <button onClick={() => setLeadStatus("new")} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E7DFD5] text-xs text-[#44403C] hover:border-[#F59E0B] disabled:opacity-50" data-testid="quote-status-reopen">
              <RotateCcw size={12} /> Reopen
            </button>
          ) : (
            <>
              <button onClick={() => setLeadStatus("won")} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-400/40 text-xs text-emerald-300 hover:bg-emerald-400/10 disabled:opacity-50" data-testid="quote-status-won"><Trophy size={12} /> Won</button>
              <button onClick={() => setLeadStatus("lost")} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-400/40 text-xs text-red-300 hover:bg-red-400/10 disabled:opacity-50" data-testid="quote-status-lost"><XCircle size={12} /> Lost</button>
              <button onClick={() => setLeadStatus("closed")} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E7DFD5] text-xs text-[#78716C] hover:border-[#E7DFD5] disabled:opacity-50" data-testid="quote-status-closed"><Archive size={12} /> Closed</button>
            </>
          )}
        </div>
        <div className="mt-5 pt-5 border-t border-[#E7DFD5]">
          <AdminNotes targetType="lead" targetId={q.id} onChanged={onChanged} testIdPrefix="quote-notes" />
        </div>
        <div className="mt-5 pt-5 border-t border-[#E7DFD5]">
          <button onClick={() => setShowHistory((v) => !v)} className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] hover:text-[#B45309] transition-colors" data-testid="quote-timeline-toggle">
            <History size={12} /> {showHistory ? "Hide history" : "Show history"}
          </button>
          {showHistory && <div className="mt-4" data-testid="quote-timeline"><TimelineList targetType="lead" targetId={q.id} testIdPrefix="quote-timeline" /></div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
